import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProduct, getProducts, getProductReviews, createReview, getBrandsConfig, getCategoryPath } from "../lib/api";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { toast } from "sonner";

const DEFAULT_STOCK_THRESHOLDS = [
    { min: 1, label: "Poslední" },
    { min: 5, label: "Skladem" },
    { min: 10, label: "Dostatek" },
];

export function useProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const { customer } = useCustomerAuth();

    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);
    const [lightbox, setLightbox] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [watchSubmitted, setWatchSubmitted] = useState(false);
    const [watchLoading, setWatchLoading] = useState(false);
    const [watchType, setWatchType] = useState("stock");
    const [targetPrice, setTargetPrice] = useState("");
    const [reviews, setReviews] = useState([]);
    const [reviewsAvg, setReviewsAvg] = useState(0);
    const [reviewsTotal, setReviewsTotal] = useState(0);
    const [addingReview, setAddingReview] = useState(false);
    const [reviewForm, setReviewForm] = useState({ author_name: "", rating: 5, title: "", body: "" });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [stickyVisible, setStickyVisible] = useState(false);
    const [brandLogo, setBrandLogo] = useState(null);
    const [categoryPath, setCategoryPath] = useState([]);
    const addToCartRef = useRef(null);
    const [betterPriceOpen, setBetterPriceOpen] = useState(false);
    const [betterPriceForm, setBetterPriceForm] = useState({ offered_price: "", message: "" });
    const [betterPriceLoading, setBetterPriceLoading] = useState(false);
    const [betterPriceSent, setBetterPriceSent] = useState(false);
    const [showConfigurator, setShowConfigurator] = useState(false);
    const [brandDescription, setBrandDescription] = useState("");
    const [customerGroupPrice, setCustomerGroupPrice] = useState(null);
    const [showBuyNow, setShowBuyNow] = useState(false);
    const [faqLd, setFaqLd] = useState(null);
    const [abMeta, setAbMeta] = useState(null);
    const [stockThresholds, setStockThresholds] = useState(DEFAULT_STOCK_THRESHOLDS);
    const [activeTab, setActiveTab] = useState(() => {
        const hash = window.location.hash.replace("#", "");
        return hash === "recenze" ? "reviews" : "description";
    });

    useEffect(() => {
        setLoading(true);
        getProduct(slug).then(r => {
            const p = r.data;
            setProduct(p);
            setActiveImg(0);
            // Track recently viewed
            import("../components/RecentlyViewed").then(m => m.trackRecentlyViewed(p)).catch(() => {});
            if (p.category_id || p.id) {
                getProducts({ category_id: p.category_id, limit: 5 }).then(rr => {
                    setRelated((rr.data?.items || []).filter(x => x.id !== p.id).slice(0, 4));
                });
            }
            getProductReviews(p.id).then(rr => {
                setReviews(rr.data?.items || []);
                setReviewsAvg(rr.data?.avg_rating || 0);
                setReviewsTotal(rr.data?.total || 0);
            }).catch(() => {});
            if (p.slug) {
                api.get(`/seo/faq-schema/${p.slug}`).then(rr => {
                    if (rr.data?.schema) setFaqLd(JSON.stringify(rr.data.schema));
                }).catch(() => {});
                api.get(`/ab-resolve/product/${p.slug}`).then(rr => {
                    if (rr.data?.variant) {
                        setAbMeta(rr.data);
                        try {
                            sessionStorage.setItem(`ab_variant_${p.slug}`, JSON.stringify({
                                variant: rr.data.variant, entity_type: "product", entity_slug: p.slug,
                            }));
                        } catch {}
                    }
                }).catch(() => {});
            }
            if (p.brand) {
                getBrandsConfig().then(rr => {
                    const cfg = (rr.data || []).find(b => b.name === p.brand);
                    if (cfg?.logo_url) setBrandLogo(cfg.logo_url);
                    else setBrandLogo(null);
                    setBrandDescription(cfg?.description || "");
                }).catch(() => { setBrandLogo(null); setBrandDescription(""); });
            } else { setBrandLogo(null); setBrandDescription(""); }
            api.get("/settings/stock-labels").then(rr => {
                if (rr.data?.thresholds?.length) setStockThresholds(rr.data.thresholds);
            }).catch(() => {});
            if (customer?.price_group) {
                import("../lib/api").then(m => m.getPriceGroups().then(rr => {
                    const grp = (rr.data || []).find(g => g.code === customer.price_group);
                    if (grp && grp.discount_percent > 0) {
                        const discountedPrice = Math.round(p.price * (1 - grp.discount_percent / 100));
                        setCustomerGroupPrice({ code: grp.code, name: grp.name, discount: grp.discount_percent, price: discountedPrice });
                    } else { setCustomerGroupPrice(null); }
                })).catch(() => setCustomerGroupPrice(null));
            } else { setCustomerGroupPrice(null); }
            if (p.category_id) {
                getCategoryPath(p.category_id).then(rr => {
                    setCategoryPath(rr.data?.path || []);
                }).catch(() => setCategoryPath([]));
            } else { setCategoryPath([]); }
            import("../lib/api").then(m => m.getThemeSettings().then(rr => {
                setShowBuyNow(rr.data?.show_buy_now ?? false);
            })).catch(() => {});
        }).catch(() => navigate("/kategorie")).finally(() => setLoading(false));
    }, [slug, navigate, customer?.price_group]);

    // Sticky CTA on scroll
    useEffect(() => {
        const handleScroll = () => {
            if (addToCartRef.current) {
                const rect = addToCartRef.current.getBoundingClientRect();
                setStickyVisible(rect.bottom < 0);
            }
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const getVolumePrice = (q) => {
        if (!product?.volume_pricing || product.volume_pricing.length < 2) return null;
        const sorted = [...product.volume_pricing].sort((a, b) => b.min_qty - a.min_qty);
        const tier = sorted.find(t => q >= t.min_qty);
        return tier ? tier.price : null;
    };

    const volumePrice = product ? getVolumePrice(qty) : null;

    const handleAddToCart = () => {
        if (!product) return;
        const effectivePrice = volumePrice || (selectedVariant?.price || product.sale_price || product.price);
        const originalPrice = product.price;
        const cartProduct = selectedVariant ? {
            ...product,
            id: `${product.id}_v${(product.variants || []).indexOf(selectedVariant)}`,
            name: `${product.name} - ${selectedVariant.variant_name}`,
            sku: selectedVariant.sku || product.sku,
            price: effectivePrice, original_price: originalPrice, sale_price: null,
        } : { ...product, price: effectivePrice, original_price: originalPrice, sale_price: null };
        addItem(cartProduct, qty);
        toast.success(`${cartProduct.name} (${qty} ks) pridan do kosiku`);
        if (abMeta?.variant && product?.slug) {
            import("../lib/api").then(m => {
                m.trackABConversion({ entity_type: "product", entity_slug: product.slug, variant: abMeta.variant, event_type: "add_to_cart" });
            }).catch(() => {});
        }
    };

    const handleWatchdog = async () => {
        if (!product) return;
        if (!customer) { toast.error("Pro hlidani se prosim prihlaste"); navigate("/prihlaseni"); return; }
        setWatchLoading(true);
        try {
            await api.post("/watchdog/subscribe", {
                product_id: product.id, email: customer.email,
                variant_name: selectedVariant?.variant_name || "",
                watch_type: watchType,
                target_price: watchType !== "stock" && targetPrice ? parseFloat(targetPrice) : null,
            });
            setWatchSubmitted(true);
            toast.success(watchType === "price" ? "Budeme vas informovat pri poklesu ceny!" : "Budeme vas informovat, az bude produkt skladem!");
        } catch (e) {
            const msg = e.response?.data?.detail || "Chyba";
            if (e.response?.data?.already_subscribed) { setWatchSubmitted(true); toast.info("Tento produkt jiz hlidate"); }
            else toast.error(msg);
        }
        setWatchLoading(false);
    };

    const handleBuyNow = () => { handleAddToCart(); navigate("/kosik"); };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.author_name.trim() || !reviewForm.body.trim()) { toast.error("Vyplnte jmeno a text recenze"); return; }
        setSubmittingReview(true);
        try {
            await createReview(product.id, reviewForm);
            toast.success("Recenze pridana, dekujeme!");
            setReviews(prev => [{ ...reviewForm, created_at: new Date().toISOString(), is_approved: true }, ...prev]);
            setReviewsTotal(t => t + 1);
            setAddingReview(false);
            setReviewForm({ author_name: "", rating: 5, title: "", body: "" });
        } catch { toast.error("Chyba pri odesilani recenze"); }
        finally { setSubmittingReview(false); }
    };

    const handleBetterPrice = async () => {
        if (!customer) { toast.error("Pro odeslani nabidky se prosim prihlaste"); navigate("/prihlaseni"); return; }
        if (!betterPriceForm.offered_price) { toast.error("Zadejte nabidkovou cenu"); return; }
        setBetterPriceLoading(true);
        try {
            await api.post("/better-price", {
                product_id: product.id, offered_price: parseFloat(betterPriceForm.offered_price),
                message: betterPriceForm.message, name: customer.name || "", email: customer.email || "", phone: customer.phone || "",
            });
            setBetterPriceSent(true);
            toast.success("Nabidka odeslana! Budeme vas kontaktovat.");
        } catch (e) { toast.error(e.response?.data?.detail || "Chyba pri odesilani"); }
        finally { setBetterPriceLoading(false); }
    };

    return {
        slug, navigate, product, related, loading, qty, setQty,
        activeImg, setActiveImg, lightbox, setLightbox,
        selectedVariant, setSelectedVariant,
        watchSubmitted, watchLoading, watchType, setWatchType, targetPrice, setTargetPrice,
        customer, reviews, reviewsAvg, reviewsTotal,
        addingReview, setAddingReview, reviewForm, setReviewForm, submittingReview,
        stickyVisible, brandLogo, categoryPath, addToCartRef,
        betterPriceOpen, setBetterPriceOpen, betterPriceForm, setBetterPriceForm,
        betterPriceLoading, betterPriceSent,
        showConfigurator, setShowConfigurator, brandDescription,
        customerGroupPrice, showBuyNow, faqLd, abMeta, stockThresholds, activeTab, setActiveTab,
        volumePrice, handleAddToCart, handleWatchdog, handleBuyNow,
        handleSubmitReview, handleBetterPrice,
    };
}
